import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { scrollToError } from '@openx/components-core/utils/scrollToError';
import { onKeyDown } from '@openx/components-form/onKeyDown';
import type { DealFormDataWithTargeting } from '@openx/types/deal';
import type { PackageFormState } from '@openx/types/package';
import omit from 'lodash/omit';
import { useCallback, useMemo, useRef, useState } from 'react';
import { type FieldValues, FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { useAuth } from '../../../components/AuthContext/context';
import { isInternalAdmin } from '../../../permissions/validators';
import { EstimatedReach } from './components/EstimatedReach/EstimatedReach';
import { StructureAndLogic } from './components/StructureAndLogic/StructureAndLogic';
import { exportType } from './constants';
import { useAudienceFormStyles } from './hooks/useAudienceFormStyles';
import { AllowedOrganizations } from './sections/AllowedOrganizations';
import { AudienceCriteria } from './sections/AudienceCriteria';
import { AudienceDetails } from './sections/AudienceDetails';
import { AudienceOptions } from './sections/AudienceOptions';
import { MultiPartnerPricing } from './sections/MultiPartnerPricing';
import { OnBehalfOfSection } from './sections/OnBehalfOfSection';
import { ProviderOptions } from './sections/ProviderOptions';
import { TargetIdTypesSection } from './sections/TargetIdTypesSection';
import { AudienceFields, type AudienceForm } from './types';
import { containsPricedMultipartnerSegment } from './utils/containsPricedMultipartnerSegment';
import { filterNonEmptyGroups } from './utils/filterNonEmptyGroups';
import { isAudienceNameConflictError } from './utils/isAudienceNameConflictError';
import { shouldShowTargetIdTypes } from './utils/marketOperatorRestrictions';
import { getBaseAudienceFormSchema, getEditAudienceFormSchema } from './utils/validationSchema';

export type AudienceLocationState = {
  deal?: DealFormDataWithTargeting;
  package?: PackageFormState;
  redirectPath?: string;
  accountId?: string;
  audienceId?: string;
  useUniqueTargeting?: boolean;
  useCuratedTemplate?: boolean;
  useAsTemplate?: boolean;
} | null;

type AudienceFormFieldsProps = {
  isCreate?: boolean;
  isEdit?: boolean;
  formValues: AudienceForm;
  onSubmitHandler?: (values: AudienceForm) => Promise<void>;
};

export const AudienceFormFields = ({
  formValues,
  isEdit = false,
  isCreate = false,
  onSubmitHandler,
}: AudienceFormFieldsProps) => {
  const { session } = useAuth();
  const { t } = useTranslation();
  const isInternalOxAdmin = isInternalAdmin(session);
  const navigate = useNavigate();
  const isEditMode = isEdit;
  const isCreateMode = isCreate;
  const isCriteriaEditable = isEditMode || isCreateMode;
  const location = useLocation();
  const isCreateFromTemplate = isCreateMode && location.state?.useAsTemplate === true;

  const audienceSchemaOptions = useMemo(
    () => ({
      isCreate: isCreateMode,
      isCreateFromTemplate,
    }),
    [isCreateMode, isCreateFromTemplate]
  );

  const validationResolver = useMemo(
    () =>
      yupResolver(
        isEditMode
          ? getEditAudienceFormSchema(t, audienceSchemaOptions)
          : getBaseAudienceFormSchema(t, audienceSchemaOptions)
      ),
    [t, isEditMode, audienceSchemaOptions]
  );

  const formMethods = useForm<AudienceForm>({
    defaultValues: {
      ...formValues,
    },
    mode: 'all',
    resolver: validationResolver,
  });

  const observableNormalizedDefinition = useWatch({
    control: formMethods.control,
    name: 'normalized_definition',
  });

  const observableDirectProvider = useWatch({
    name: AudienceFields.DirectAudienceProvider,
    control: formMethods.control,
  });
  const observableExportType = useWatch({ name: AudienceFields.ExportType, control: formMethods.control });
  const currentExportType = formMethods.getValues(AudienceFields.ExportType) ?? observableExportType;

  const [showExcludeSection, setShowExcludeSection] = useState<boolean>(
    filterNonEmptyGroups(observableNormalizedDefinition.excludes.groups).length > 0
  );

  const hideCriteria =
    currentExportType === null || (currentExportType !== exportType.OA_MATCH && observableDirectProvider === null);
  const showTargetIdTypes = useMemo(
    () => shouldShowTargetIdTypes(currentExportType, observableNormalizedDefinition),
    [currentExportType, observableNormalizedDefinition]
  );

  const observableAccountId = formMethods.watch('account_id');

  const formRef = useRef<HTMLFormElement>(null);

  const scrollToFirstError = useCallback((errors: FieldValues = {}) => {
    scrollToError(errors);
  }, []);

  const onSubmit = async (values: AudienceForm) => {
    try {
      await onSubmitHandler?.(values);
    } catch (error) {
      if (isAudienceNameConflictError(error)) {
        formMethods.setError('name', {
          type: 'server',
          message: t('Audience with such name already exists'),
        });
        scrollToFirstError({
          name: { message: t('Audience with such name already exists') },
        });
        return;
      }
      // biome-ignore lint/suspicious/noConsole: logging error in console
      console.error(error);
    }
  };

  const showMultiPartnerPricing = useMemo(() => {
    if (currentExportType !== exportType.OA_MATCH) {
      return false;
    }

    if (containsPricedMultipartnerSegment(observableNormalizedDefinition.includes, observableAccountId)) {
      return true;
    }

    return containsPricedMultipartnerSegment(observableNormalizedDefinition.excludes, observableAccountId);
  }, [observableNormalizedDefinition, observableAccountId, currentExportType]);

  const {
    formState: { isDirty, isSubmitting },
  } = formMethods;
  const isSaveDisabled = isEditMode ? !isDirty || isSubmitting : isSubmitting;

  const styles = useAudienceFormStyles();

  const handleCancel = useCallback(() => {
    if (location.state?.redirectPath) {
      navigate(location.state.redirectPath, { state: omit(location.state, ['redirectPath']) });
    } else {
      navigate(-1);
    }
  }, [location.state, navigate]);

  return (
    <FormProvider {...formMethods}>
      <Box
        component="form"
        ref={formRef}
        onSubmit={formMethods.handleSubmit(onSubmit, scrollToFirstError)}
        onKeyDown={onKeyDown}
        sx={{ height: '100%' }}
      >
        <Box sx={styles.container}>
          <Box sx={styles.mainContent}>
            {isInternalOxAdmin && <OnBehalfOfSection isEdit={isEditMode} />}
            <AudienceOptions isEdit={isEditMode} />
            <ProviderOptions isEdit={isEditMode} />
            <AudienceDetails />
            <AudienceCriteria
              isEdit={isCriteriaEditable}
              isCreate={isCreateMode}
              isCreateFromTemplate={isCreateFromTemplate}
              hideCriteria={hideCriteria}
              showExcludeSection={showExcludeSection}
              setShowExcludeSection={setShowExcludeSection}
              showDivider={showTargetIdTypes || showMultiPartnerPricing || isEditMode}
            />
            <TargetIdTypesSection />
            {isEditMode && <AllowedOrganizations showDivider={showMultiPartnerPricing} />}
            {showMultiPartnerPricing && <MultiPartnerPricing />}
          </Box>

          <Box sx={styles.legendContainer}>
            <EstimatedReach />
            <StructureAndLogic
              hideCriteria={hideCriteria}
              showExcludeSection={showExcludeSection}
              isEdit={isCriteriaEditable}
              isCreate={isCreateMode}
              isCreateFromTemplate={isCreateFromTemplate}
            />
          </Box>

          <Box sx={styles.bottomBar}>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              loading={isSubmitting}
              disabled={isSaveDisabled}
              data-test="submit-button"
            >
              {t('Save')}
            </Button>
            <Button sx={{ color: 'text.secondary' }} variant="text" onClick={handleCancel} data-test="cancel-button">
              {t('Cancel')}
            </Button>
          </Box>
        </Box>
      </Box>
    </FormProvider>
  );
};
